import { defineMessages } from 'react-intl';

/**
 * Steam compatibility dimensions.
 *
 * Single source of truth for the four Valve compatibility ratings Fanatical
 * surfaces (Steam Deck, Steam Machine, SteamOS, Steam Frame). Consumed by the
 * PDP support row, SteamCompatPage, SteamCompatPills, and SteamCompatInformation.
 *
 * Each entry:
 *   key             Matches the schema field prefix on product.steam
 *                   (e.g. 'deck' → deck_support, deck_details, deck_note).
 *                   Also the `dimension` prop passed to SteamCompatInformation.
 *   route           Category listing URL. Used for the PDP label link, the
 *                   pills nav, and route registration.
 *   pdpDisplay      Whether the PDP support row shows this dimension.
 *   categoryListing Whether the category page renders. When false, the route
 *                   still registers but SteamCompatPage returns <NotFound />.
 *                   Flip to true once the listing is ready to launch.
 *   analyticsTag    Passed to StaticSearch for the category listing.
 *   labelMsg        PDP row label (spread from defineMessages so babel-plugin-
 *                   react-intl can extract it).
 *   seo             { title, fullTitle, desc, seoBlurb? } spread into StaticSearch props.
 * Pill labels (All / Playable / Verified) are shared across dimensions and
 * live in SteamCompatPills, not here.
 */

export default [
  {
    key: 'deck',
    route: '/steam-deck-games',
    pdpDisplay: true,
    categoryListing: true,
    analyticsTag: 'frontend.fetchSteamDeckGames',
    ...defineMessages({
      labelMsg: { id: 'steam_compat.steam_deck_support', defaultMessage: 'Steam Deck Support' },
    }),
    seo: defineMessages({
      title: { id: 'steam-deck.seotitle', defaultMessage: 'Steam Deck Games' },
      fullTitle: { id: 'steam-deck.socialtitle', defaultMessage: 'Steam Deck Games | Exclusive PC Steam Discounts' },
      desc: { id: 'steam-deck.seodesc', defaultMessage: 'Give your Steam Deck a boost with our top deals on Games for Steam Deck and PC. Featuring everything from top AAA titles to hidden Indie gems, check back regularly for our latest offers.' },
    }),
  },
  {
    key: 'machine',
    route: '/steam-machine-games',
    pdpDisplay: true,
    categoryListing: true,
    analyticsTag: 'frontend.fetchSteamMachineGames',
    ...defineMessages({
      labelMsg: { id: 'steam_compat.steam_machine_support', defaultMessage: 'Steam Machine Support' },
    }),
    seo: defineMessages({
      title: { id: 'steam-machine.seotitle', defaultMessage: 'Steam Machine Games' },
      fullTitle: { id: 'steam-machine.socialtitle', defaultMessage: 'Best Steam Machine Games & Deals | Verified Steam Keys' },
      seoBlurb: { id: 'steam-machine.seodesc', defaultMessage: 'Discover top Steam Machine compatible games and exclusive bundle deals. Get 100% official Steam keys verified for your Valve living room console at deep discounts!' },
      desc: { id: 'steam-machine.desc', defaultMessage: 'Bring the ultimate power of PC gaming directly to your living room with the absolute best Steam Machine games and exclusive bundle deals. Find games optimized and verified to run seamlessly on Valve’s custom architecture and SteamOS, delivering a true plug-and-play console experience right out of the box' },
    }),
  },
  {
    key: 'steamos',
    route: '/steamos-games',
    pdpDisplay: true,
    categoryListing: false,
    analyticsTag: 'frontend.fetchSteamOSGames',
    ...defineMessages({
      labelMsg: { id: 'steam_compat.steamos_support', defaultMessage: 'SteamOS Support' },
    }),
    seo: defineMessages({
      title: { id: 'steamos.seotitle', defaultMessage: 'SteamOS Games' },
      fullTitle: { id: 'steamos.socialtitle', defaultMessage: 'SteamOS Games | Exclusive PC Steam Discounts' },
      desc: { id: 'steamos.seodesc', defaultMessage: 'Explore top deals on Games verified and playable on SteamOS. Featuring everything from top AAA titles to hidden Indie gems, check back regularly for our latest offers.' },
    }),
  },
  {
    key: 'frame',
    route: '/steam-frame-games',
    pdpDisplay: false,
    categoryListing: false,
    analyticsTag: 'frontend.fetchSteamFrameGames',
    ...defineMessages({
      labelMsg: { id: 'steam_compat.steam_frame_support', defaultMessage: 'Steam Frame Support' },
    }),
    seo: defineMessages({
      title: { id: 'steam-frame.seotitle', defaultMessage: 'Steam Frame Games' },
      fullTitle: { id: 'steam-frame.socialtitle', defaultMessage: 'Steam Frame Games | Exclusive PC Steam Discounts' },
      desc: { id: 'steam-frame.seodesc', defaultMessage: 'Browse top deals on Games verified and playable on Steam Frame. Featuring everything from top AAA titles to hidden Indie gems, check back regularly for our latest offers.' },
    }),
  },
];
